'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

type SignUpRole = 'user' | 'artist';

interface UserSignUpData {
  email: string;
  password: string;
  confirmPassword: string;
  inviteCode?: string;
  agreeTerms: boolean;
}

interface ArtistSignUpData extends UserSignUpData {
  studioName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
}

interface InviteSettings {
  userRequired: boolean;
  userCode: string;
  artistRequired: boolean;
  artistCode: string;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [role, setRole] = useState<SignUpRole>('user');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [inviteSettings, setInviteSettings] = useState<InviteSettings>({
    userRequired: false,
    userCode: '',
    artistRequired: false,
    artistCode: '',
  });

  const { signUp, signIn } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    const fetchInviteSettings = async () => {
      try {
        const { data } = await supabase
          .from('invite_code_settings')
          .select('*')
          .limit(1)
          .maybeSingle();
        if (data) {
          setInviteSettings({
            userRequired: data.user_invite_required ?? false,
            userCode: data.user_invite_code ?? '',
            artistRequired: data.artist_invite_required ?? false,
            artistCode: data.artist_invite_code ?? '',
          });
        }
      } catch {
        // Use defaults if fetch fails
      }
    };
    fetchInviteSettings();
  }, []);

  const { register, handleSubmit, watch, formState: { errors }, setError } = useForm<ArtistSignUpData>();
  const password = watch('password');

  const needsInvite = role === 'user' ? inviteSettings.userRequired : inviteSettings.artistRequired;
  const correctCode = role === 'user' ? inviteSettings.userCode : inviteSettings.artistCode;

  const onSubmit = async (data: ArtistSignUpData) => {
    setSignUpError('');
    if (needsInvite && data.inviteCode !== correctCode) {
      setError('inviteCode', { message: 'Invalid invite code. Check with your referrer.' });
      return;
    }
    setIsLoading(true);
    try {
      const metadata: Record<string, any> = {
        full_name: role === 'artist' ? data.studioName : data.email.split('@')[0],
        role,
      };
      if (role === 'artist') {
        metadata.studio_name = data.studioName;
        metadata.phone = data.phone;
        metadata.address = data.address;
        metadata.city = data.city;
        metadata.state = data.state;
      }
      await signUp(data.email, data.password, metadata);

      // If artist, store pending artist data for post-login creation
      if (role === 'artist') {
        if (typeof window !== 'undefined') {
          localStorage.setItem('pending_artist_profile', JSON.stringify({
            studio_name: data.studioName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            state: data.state,
          }));
        }
      }

      // Try to sign in immediately; may fail if email confirmation is required
      let signedIn = false;
      try {
        await signIn(data.email, data.password);
        signedIn = true;
      } catch {
        // Email confirmation required — show success state and let user confirm
        signedIn = false;
      }

      setSuccess(true);
      if (signedIn) {
        router.refresh();
        setTimeout(() => {
          if (role === 'artist') {
            router.push('/artist-dashboard');
          } else {
            router.push('/user-dashboard');
          }
        }, 1500);
      }
    } catch (err: any) {
      setSignUpError(err?.message || 'Sign up failed. Please try again.');
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-positive/10 border border-positive/20 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckIcon" size={28} variant="outline" className="text-positive" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Account Created!</h2>
        <p className="text-muted-foreground text-sm mb-6">
          Welcome to TryInk. Check your email to confirm your account, then sign in to get started.
        </p>
        <button onClick={onSwitchToLogin} className="btn-primary w-full">
          Sign In Now
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Create your account</h1>
        <p className="text-muted-foreground text-sm">Join TryInk — free forever for personal use</p>
      </div>

      {signUpError && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">
          {signUpError}
        </div>
      )}

      {/* Role selector */}
      <div className="flex bg-secondary rounded-xl p-1 mb-6">
        {(['user', 'artist'] as SignUpRole[]).map((r) => (
          <button
            key={`role-${r}`}
            type="button"
            onClick={() => setRole(r)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2 ${
              role === r ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={r === 'user' ? 'UserIcon' : 'PaintBrushIcon'} size={15} variant="outline" />
            {r === 'user' ? "I'm a Client" : "I'm an Artist"}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {role === 'artist' && (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Studio / Artist Name</label>
              <p className="text-xs text-muted-foreground mb-1.5">Your public artist name or studio name</p>
              <input
                type="text"
                className={`input-field ${errors.studioName ? 'border-negative' : ''}`}
                placeholder="Ink & Iron Studio"
                {...register('studioName', { required: 'Studio name is required' })}
              />
              {errors.studioName && <p className="mt-1.5 text-xs text-negative">{errors.studioName.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
                <input
                  type="tel"
                  className={`input-field ${errors.phone ? 'border-negative' : ''}`}
                  placeholder="+1 (512) 000-0000"
                  {...register('phone', { required: 'Phone is required' })}
                />
                {errors.phone && <p className="mt-1.5 text-xs text-negative">{errors.phone.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">City</label>
                <input
                  type="text"
                  className={`input-field ${errors.city ? 'border-negative' : ''}`}
                  placeholder="Austin"
                  {...register('city', { required: 'City is required' })}
                />
                {errors.city && <p className="mt-1.5 text-xs text-negative">{errors.city.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Studio Address</label>
              <input
                type="text"
                className={`input-field ${errors.address ? 'border-negative' : ''}`}
                placeholder="123 South Congress Ave, Austin TX 78704"
                {...register('address', { required: 'Address is required' })}
              />
              {errors.address && <p className="mt-1.5 text-xs text-negative">{errors.address.message}</p>}
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
          <input
            type="email"
            className={`input-field ${errors.email ? 'border-negative' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1.5 text-xs text-negative">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, at least one uppercase and number</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`input-field pr-10 ${errors.password ? 'border-negative' : ''}`}
              placeholder="Create a strong password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
            </button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs text-negative">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Confirm Password</label>
          <input
            type="password"
            className={`input-field ${errors.confirmPassword ? 'border-negative' : ''}`}
            placeholder="Repeat your password"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === password || 'Passwords do not match',
            })}
          />
          {errors.confirmPassword && <p className="mt-1.5 text-xs text-negative">{errors.confirmPassword.message}</p>}
        </div>

        {needsInvite && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Invite Code
              <span className="ml-2 badge bg-warning/10 text-warning">Required</span>
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              {role === 'user' ? 'User' : 'Artist'} sign-ups currently require an invite code.
            </p>
            <input
              type="text"
              maxLength={6}
              className={`input-field font-mono tracking-[0.4em] text-center text-lg ${errors.inviteCode ? 'border-negative' : ''}`}
              placeholder="000000"
              {...register('inviteCode', {
                required: 'Invite code is required',
                minLength: { value: 6, message: 'Code must be 6 digits' },
                maxLength: { value: 6, message: 'Code must be 6 digits' },
                pattern: { value: /^\d{6}$/, message: 'Code must be 6 digits' },
              })}
            />
            {errors.inviteCode && <p className="mt-1.5 text-xs text-negative">{errors.inviteCode.message}</p>}
          </div>
        )}

        <div className="flex items-start gap-2.5 pt-1">
          <input
            type="checkbox"
            id="agreeTerms"
            className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
            {...register('agreeTerms', { required: 'You must agree to the terms' })}
          />
          <label htmlFor="agreeTerms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
            I agree to TryInk&apos;s{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>{' '}
            and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          </label>
        </div>
        {errors.agreeTerms && <p className="text-xs text-negative">{errors.agreeTerms.message}</p>}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-base"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Creating account...
            </>
          ) : (
            `Create ${role === 'artist' ? 'Artist' : ''} Account`
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary hover:underline font-medium">
          Sign in
        </button>
      </p>
    </div>
  );
}